import React from 'react'
import clsx from 'clsx'
import Typography from '@mui/material/Typography'
import { bindActionCreators, Dispatch } from 'redux'
import { connect } from 'react-redux'
import Button from 'components/Button'
import Link from 'components/Link'
import { CircularLoading } from 'components/Loading'
import { link } from 'appConstants'
import { actions } from '../duck'
import { RootActionType, RootState } from 'duck'
import { useBreakpoint } from 'utils/Hooks'
import { ReduxProps } from 'utils/Types'
import { appActions, appSelectors } from 'duck/AppDuck'
import { apiSelectors } from 'duck/ApiDuck'
import { TextTransform } from 'utils/TextUtils'
import { dialogActions, FormDialog, InformationDialog } from 'duck/AppDuck/DialogDuck'
import { selectors } from 'containers/NFTSellPanel/duck'
import styles from './NFTSellForm.module.scss'

const mapStateToProps = (state: RootState) => ({
  submitLoading: selectors.submitLoading(state),
  isResubmitForm: selectors.isResubmitForm(state),
  network: selectors.formData(state).network,
  currentMineProject: selectors.currentMineProject(state),
  connectToMetaMaskLoading: appSelectors.connectToMetaMaskLoading(state),
  currentUserAddress: apiSelectors.currentUserAddress(state),
  retrieveConfigLoading: apiSelectors.loading['artMine.retrieveConfig'](state),
  createArtMineProjectLoading: apiSelectors.loading['artMine.createArtMineProject'](state),
  retrieveSignatureLoading: apiSelectors.loading['artMine.retrieveSignature'](state)
})

const mapDispatchToProps = (dispatch: Dispatch<RootActionType>) =>
  bindActionCreators(
    {
      addDialogOverlay: dialogActions.addDialogOverlay,
      connectToMetamask: appActions.metamask.connectToMetamask
    },
    dispatch
  )
type FormSubmitProps = ReduxProps<typeof mapDispatchToProps, typeof mapStateToProps>

const FormSubmit: React.FC<FormSubmitProps> = props => {
  const {
    addDialogOverlay,
    connectToMetamask,
    network,
    currentMineProject,
    isResubmitForm,
    submitLoading,
    createArtMineProjectLoading,
    retrieveSignatureLoading,
    retrieveConfigLoading,
    currentUserAddress,
    connectToMetaMaskLoading
  } = props

  const { isSmallscreenAbove } = useBreakpoint()

  const transactionLoading = createArtMineProjectLoading || submitLoading
  const isProcessing = currentMineProject?.isProcessing

  return (
    <div
      className={clsx('mx-auto flex flex-col', styles.FormWrapper, !isSmallscreenAbove && 'pt-3')}
    >
      <div className="mb-12">
        <Typography variant="h5" className="mb-4">
          {isResubmitForm ? `Resubmit` : `Step 3: Submit`}
        </Typography>

        <Typography variant="subtitle2" className="mb-6 text-white-med-on-dark">
          By submitting this project, I accept the{' '}
          <Link href={link.TOC} target="_blank">
            Terms and Conditions
          </Link>
          .
        </Typography>

        <div>
          <Typography variant="body1">Smart Contract Terms</Typography>

          <ul className="mb-6 mt-0 pl-5 text-white-med-on-dark">
            <li>
              <Typography variant="subtitle2">
                Primary Sale: 85% Creator (You), 15% Playform
              </Typography>
            </li>

            <li>
              <Typography variant="subtitle2">
                Secondary Sales: 8.5% Creator (You), 1.5% Playform
              </Typography>
            </li>
          </ul>

          <Typography variant="subtitle2" className="text-white-med-on-dark">
            {!currentUserAddress ? `Connect to MetaMask to continue. ` : null}
            You may also be required to pay an additional transaction fee to the blockchain network
            to execute the smart contract. Fees vary by network.
          </Typography>
        </div>
      </div>

      <Button
        disabled={Boolean(connectToMetaMaskLoading || retrieveConfigLoading)}
        className="mb-4 mt-auto"
        color="primary"
        variant="outlined"
        onClick={() => {
          if (currentUserAddress) {
            addDialogOverlay({
              [FormDialog.METAMASK_WALLET]: {
                dialogName: FormDialog.METAMASK_WALLET
              }
            })
          } else {
            connectToMetamask({ network })
          }
        }}
        fullWidth
      >
        {connectToMetaMaskLoading
          ? 'Connecting...'
          : currentUserAddress
            ? `CONNECTED TO ${TextTransform.trimAddress(currentUserAddress)}`
            : `Connect To Metamask`}
      </Button>

      {isResubmitForm ? (
        <>
          <Button
            className="mt-auto"
            color="secondary"
            disabled={Boolean(
              !currentUserAddress ||
                createArtMineProjectLoading ||
                retrieveSignatureLoading ||
                submitLoading ||
                isProcessing
            )}
            variant="contained"
            size="small"
            onClick={() => {
              addDialogOverlay({
                [InformationDialog.INCREASE_GAS]: {
                  dialogName: InformationDialog.INCREASE_GAS,
                  continueAction: actions.retrySubmit(),
                  continueText: 'RESUBMIT'
                }
              })
            }}
            fullWidth
          >
            {transactionLoading || isProcessing ? (
              <CircularLoading size={28} disableShrink loading />
            ) : (
              'RESUBMIT'
            )}
          </Button>

          {isProcessing ? (
            <Typography variant="body1" color="gray" className="mt-4 text-center" component="div">
              <i>
                This project is being processed. If the processing failed, you can always resubmit
                the project from My NFT list page.
              </i>
            </Typography>
          ) : null}
        </>
      ) : (
        <Button
          className="mt-auto"
          color="secondary"
          disabled={Boolean(
            !currentUserAddress ||
              createArtMineProjectLoading ||
              retrieveSignatureLoading ||
              submitLoading
          )}
          variant="contained"
          size={transactionLoading ? 'small' : undefined}
          onClick={() => {
            addDialogOverlay({
              [InformationDialog.INCREASE_GAS]: {
                dialogName: InformationDialog.INCREASE_GAS,
                continueAction: actions.acceptSubmit(),
                continueText: 'ACCEPT AND SUBMIT'
              }
            })
          }}
          fullWidth
        >
          {transactionLoading ? (
            <CircularLoading size={28} disableShrink loading />
          ) : (
            'ACCEPT AND SUBMIT'
          )}
        </Button>
      )}
    </div>
  )
}
export default connect(mapStateToProps, mapDispatchToProps)(FormSubmit)
